import * as formatHelper from "../../helpers/formatHelper";
import * as dispenseTypes from "../../constants/dispenseTypes";

/**
 * @class Canister
 */
export default class Canister {
    /**
     * @param {Number}  id
     * @param {String}  name
     * @param {Number}  currentLevel    Current level in RCC unit
     * @param {Number}  currentLevelMl  Current level in ml
     * @param {Number}  minimumLevel    Minimum level in RCC unit
     * @param {Number}  minimumLevelMl  Minimum level in ml
     * @param {Number}  warningLevel    Warning level in RCC unit
     * @param {Number}  warningLevelMl  Warning level in ml
     * @param {Number}  capacity        Capacity of canister in RCC unit
     * @param {Number}  capacityMl      Capacity of canister in ml
     * @param {Object}  colorant
     * @param {Array}   channels
     * @param {Boolean} enabled
     * @param {String}  barcode
     * @param {String}  ufi
     * @param {String}  batch
     * @param {Number}  threshold
     * @param {Number}  unitCoefficient
     * @param {Number}  maxDispenseLevel
     * @param {Number}  dispenseLogicType
     * @param {Array}   colorantPackages
     */
    constructor(
        id,
        name,
        currentLevel,
        currentLevelMl,
        minimumLevel,
        minimumLevelMl,
        warningLevel,
        warningLevelMl,
        capacity,
        capacityMl,
        colorant,
        channels,
        enabled,
        barcode = "",
        ufi = null,
        batch = null,
        threshold = 0,
        unitCoefficient = 0,
        maxDispenseLevel = 0,
        dispenseLogicType = dispenseTypes.TINT_ONLY_MAX_TINTABLE_AMOUNT,
        colorantPackages = []
    ) {
        this._id = id;
        this._name = name;
        this._currentLevel = currentLevel;
        this._currentLevelMl = currentLevelMl;
        this._minimumLevel = minimumLevel;
        this._minimumLevelMl = minimumLevelMl;
        this._warningLevel = warningLevel;
        this._warningLevelMl = warningLevelMl;
        this._capacity = capacity;
        this._capacityMl = capacityMl;
        this._colorant = colorant;
        this._channels = channels;
        this._enabled = enabled;
        this._barcode = barcode;
        this._ufi = ufi;
        this._batch = batch;
        this._threshold = threshold;
        this._currentLevelPercent =
            this._capacityMl > 0 ? Math.round((this._currentLevelMl / this._capacityMl) * 100) / 100 : 0;
        this._unitCoefficient = unitCoefficient;
        this._maxDispenseLevel = maxDispenseLevel;
        this._dispenseLogicType = dispenseLogicType;
        this._colorantPackages = colorantPackages;

        if (typeof this._id !== "number") {
            throw new Error("Invalid datatype of ID - Canister");
        }
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @return {String}
     */
    get name() {
        return this._name;
    }

    /**
     * @param {String} name
     */
    set name(name) {
        this._name = name;
    }

    /**
     * @return {Number}
     */
    get currentLevel() {
        return this._currentLevel;
    }

    /**
     * @param {Number} currentLevel
     */
    set currentLevel(currentLevel) {
        this._currentLevel = currentLevel;
    }

    /**
     * @return {Number}
     */
    get currentLevelMl() {
        return this._currentLevelMl;
    }

    /**
     * @param {Number} currentLevelMl
     */
    set currentLevelMl(currentLevelMl) {
        this._currentLevelMl = currentLevelMl;
    }

    /**
     * @return {Number}
     */
    get minimumLevel() {
        return this._minimumLevel;
    }

    /**
     * @param {Number} minimumLevel
     */
    set minimumLevel(minimumLevel) {
        this._minimumLevel = minimumLevel;
    }

    /**
     * @return {Number}
     */
    get minimumLevelMl() {
        return this._minimumLevelMl;
    }

    /**
     * @param {Number} minimumLevelMl
     */
    set minimumLevelMl(minimumLevelMl) {
        this._minimumLevelMl = minimumLevelMl;
    }

    /**
     * @return {Number}
     */
    get warningLevel() {
        return this._warningLevel;
    }

    /**
     * @param {Number} warningLevel
     */
    set warningLevel(warningLevel) {
        this._warningLevel = warningLevel;
    }

    /**
     * @return {Number}
     */
    get warningLevelMl() {
        return this._warningLevelMl;
    }

    /**
     * @param {Number} warningLevelMl
     */
    set warningLevelMl(warningLevelMl) {
        this._warningLevelMl = warningLevelMl;
    }

    /**
     * @return {Number}
     */
    get capacity() {
        return this._capacity;
    }

    /**
     * @param {Number} capacity
     */
    set capacity(capacity) {
        this._capacity = capacity;
    }

    /**
     * @return {Number}
     */
    get capacityMl() {
        return this._capacityMl;
    }

    /**
     * @param {Number} capacityMl
     */
    set capacityMl(capacityMl) {
        this._capacityMl = capacityMl;
    }

    /**
     * @return {Object}
     */
    get colorant() {
        return this._colorant;
    }

    /**
     * @param {Object} colorant
     */
    set colorant(colorant) {
        this._colorant = colorant;
    }

    /**
     * @return {Array}
     */
    get channels() {
        return this._channels;
    }

    /**
     * @param {Array} channels
     */
    set channels(channels) {
        this._channels = channels;
    }

    /**
     * @return {Boolean}
     */
    get enabled() {
        return this._enabled;
    }

    /**
     * @param {Boolean} enabled
     */
    set enabled(enabled) {
        this._enabled = enabled;
    }

    /**
     * @return {String}
     */
    get barcode() {
        return this._barcode;
    }

    /**
     * @param {String} barcode
     */
    set barcode(barcode) {
        this._barcode = barcode;
    }

    /**
     * @return {String} ufi
     */
    get ufi() {
        return this._ufi;
    }

    /**
     * @param {String} ufi
     */
    set ufi(ufi) {
        this._ufi = ufi;
    }

    /**
     * @return {String} batch
     */
    get batch() {
        return this._batch;
    }

    /**
     * @param {String} batch
     */
    set batch(batch) {
        this._batch = batch;
    }

    /**
     * @return {Number}
     */
    get threshold() {
        return this._threshold;
    }

    /**
     * @param {Number} threshold
     */
    set threshold(threshold) {
        this._threshold = threshold;
    }

    /**
     * @return {Number}
     */
    get currentLevelPercent() {
        return this._currentLevelPercent;
    }

    /**
     * @param {Number} currentLevelPercent
     */
    set currentLevelPercent(currentLevelPercent) {
        this._currentLevelPercent = currentLevelPercent;
    }

    /**
     * @return {Number}
     */
    get unitCoefficient() {
        return this._unitCoefficient;
    }

    /**
     * @param {Number} unitCoefficient
     */
    set unitCoefficient(unitCoefficient) {
        this._unitCoefficient = unitCoefficient;
    }

    /**
     * @return {Number}
     */
    get maxDispenseLevel() {
        return this._maxDispenseLevel;
    }

    /**
     * @param {Number} maxDispenseLevel
     */
    set maxDispenseLevel(maxDispenseLevel) {
        this._maxDispenseLevel = maxDispenseLevel;
    }

    /**
     * @return {Number}
     */
    get dispenseLogicType() {
        return this._dispenseLogicType;
    }

    /**
     * @param {Number} dispenseLogicType
     */
    set dispenseLogicType(dispenseLogicType) {
        this._dispenseLogicType = dispenseLogicType;
    }

    /**
     * @return {Array}
     */
    get colorantPackages() {
        return this._colorantPackages;
    }

    /**
     * @param {Array} colorantPackages
     */
    set colorantPackages(colorantPackages) {
        this._colorantPackages = colorantPackages;
    }

    /**
     * @return {String}
     */
    toString() {
        return "Canister (" + this._id + ")";
    }

    /**
     * @return {String}
     */
    formatCurrentLevel() {
        return formatHelper.formatDecimal(this._currentLevel);
    }

    /**
     * @return {String}
     */
    formatMaxDispenseLevel() {
        return formatHelper.formatDecimal(this._maxDispenseLevel);
    }

    /**
     * @return {String}
     */
    formatCurrentLevelPercent() {
        return formatHelper.formatPercent(this._currentLevelPercent);
    }

    /**
     * @return {Object}
     */
    clone() {
        return new Canister(
            this._id,
            this._name,
            this._currentLevel,
            this._currentLevelMl,
            this._minimumLevel,
            this._minimumLevelMl,
            this._warningLevel,
            this._warningLevelMl,
            this._capacity,
            this._capacityMl,
            this._colorant,
            this._channels,
            this._enabled,
            this._barcode,
            this._ufi,
            this._batch,
            this._threshold,
            this._unitCoefficient,
            this._maxDispenseLevel,
            this._dispenseLogicType,
            this._colorantPackages
        );
    }

    /**
     * Clones canister object and updates level properties.
     *
     * @param   {Number} currentLevel
     * @param   {Number} currentLevelMl
     * @param   {Number} minimumLevel
     * @param   {Number} minimumLevelMl
     * @param   {Number} warningLevel
     * @param   {Number} warningLevelMl
     * @return  {Object}
     */
    updateLevels = (currentLevel, currentLevelMl, minimumLevel, minimumLevelMl, warningLevel, warningLevelMl) => {
        let canister = this.clone();
        canister.currentLevel = currentLevel;
        canister.currentLevelMl = currentLevelMl;
        canister.currentLevelPercent =
            this._capacityMl > 0 ? Math.round((currentLevelMl / this._capacityMl) * 100) / 100 : 0;
        canister.minimumLevel = minimumLevel;
        canister.minimumLevelMl = minimumLevelMl;
        canister.warningLevel = warningLevel;
        canister.warningLevelMl = warningLevelMl;

        return canister;
    };

    /**
     * Clones canister object and updates properties.
     *
     * @param   {Object} update
     * @return  {Object}
     */
    updateData = (update) => {
        let canister = this.clone();
        canister.currentLevel = update.currentLevel;
        canister.currentLevelMl = update.currentLevelMl;
        canister.currentLevelPercent =
            canister.capacityMl > 0 ? Math.round((update.currentLevelMl / canister.capacityMl) * 100) / 100 : 0;
        canister.minimumLevel = update.minimumLevel;
        canister.minimumLevelMl = update.minimumLevelMl;
        canister.warningLevel = update.warningLevel;
        canister.warningLevelMl = update.warningLevelMl;
        canister.maxDispenseLevel = update.maxDispenseLevel;
        canister.capacity = update.capacity;
        canister.capacityMl = update.capacityMl;
        canister.ufi = update.ufi;
        canister.batch = update.batch;
        canister.threshold = update.threshold;

        return canister;
    };

    updateColorantPackages = (colorantPackages) => {
        if (colorantPackages.length === 0) {
            return this;
        }

        let canister = this.clone();
        canister.colorantPackages = colorantPackages;

        return canister;
    };

    /**
     * @return {Number}
     */
    currentLevelPercentage = () => {
        if (this._capacity === 0) {
            return 0;
        }

        return (this._currentLevel / this._capacity) * 100;
    };

    /**
     * @return {Number}
     */
    minimumLevelPercentage = () => {
        if (this._capacity === 0) {
            return 0;
        }

        return (this._minimumLevel / this._capacity) * 100;
    };

    /**
     * @return {Number}
     */
    warningLevelPercentage = () => {
        if (this._capacity === 0) {
            return 0;
        }

        return (this._warningLevel / this._capacity) * 100;
    };
}
